export const CRM_FIELD_TYPE = {
  TEXT: 0,
  LONG_TEXT: 1,
  NUMBER: 2,
  CURRENCY: 3,
  DATE: 4,
  CHECKBOX: 5,
  SINGLE_SELECT: 6,
  MULTI_SELECT: 7,
  READ_ONLY: 8,
} as const;

export type CrmFieldType = (typeof CRM_FIELD_TYPE)[keyof typeof CRM_FIELD_TYPE];

export const CRM_FIELD_TYPE_LABEL: Record<CrmFieldType, string> = {
  [CRM_FIELD_TYPE.TEXT]: "Text",
  [CRM_FIELD_TYPE.LONG_TEXT]: "Long text",
  [CRM_FIELD_TYPE.NUMBER]: "Number",
  [CRM_FIELD_TYPE.CURRENCY]: "Currency",
  [CRM_FIELD_TYPE.DATE]: "Date",
  [CRM_FIELD_TYPE.CHECKBOX]: "Checkbox",
  [CRM_FIELD_TYPE.SINGLE_SELECT]: "Single select",
  [CRM_FIELD_TYPE.MULTI_SELECT]: "Multi select",
  [CRM_FIELD_TYPE.READ_ONLY]: "Read only",
};

/** Field types whose answer is picked from CRMFieldOption rows */
export const OPTION_BACKED_FIELD_TYPES: CrmFieldType[] = [
  CRM_FIELD_TYPE.SINGLE_SELECT,
  CRM_FIELD_TYPE.MULTI_SELECT,
];

export type CrmFieldOption = {
  uuid: string;
  label: string;
  value: string;
  order: number;
};

/** The answer shape differs per field type; null always means "no value yet" */
export type CrmFieldValue = string | number | boolean | string[] | null;

export type CrmField = {
  uuid: string;
  placement_id: string;
  section_id: string;
  instance_id?: string | null;
  field_key: string;
  label: string;
  field_type: CrmFieldType;
  help_text: string | null;
  is_read_only: boolean;
  is_required: boolean;
  is_default: boolean;
  config: Record<string, unknown>;
  order: number;
  options: CrmFieldOption[];
  value?: CrmFieldValue;
  value_display?: string | null;
  value_uuid?: string | null;
  updated_at?: string | null;
  updated_by?: string | null;
};

export type CrmSectionInstance = {
  instance_id: string;
  order: number;
  fields: CrmField[];
};

export type CrmSection = {
  uuid: string;
  key: string | null;
  tab_id?: string;
  title: string;
  order: number;
  column_count: number;
  is_default: boolean;
  is_repeatable: boolean;
  fields: CrmField[];
  instances?: CrmSectionInstance[];
};

export type CrmTab = {
  uuid: string;
  key: string | null;
  title: string;
  order: number;
  is_default: boolean;
  sections: CrmSection[];
};

export type CrmTabSummary = Omit<CrmTab, "sections">;

export type CrmEntityLayout = {
  entity_id: string;
  company_group_id: string;
  tabs: CrmTab[];
};

export type CrmLayout = {
  tabs: CrmTab[];
};

export type CrmFieldValueInput = {
  field_id: string;
  instance_id?: string;
  value: CrmFieldValue;
};

export type CrmOptionInput = {
  label: string;
  value?: string;
};

export type CrmFieldNodeInput = {
  parent_id?: string;
  node_id?: string;
  existing_field_id?: string;
  field_key?: string;
  label?: string;
  field_type?: CrmFieldType;
  help_text?: string | null;
  is_read_only?: boolean;
  is_required?: boolean;
  order?: number;
  options?: CrmOptionInput[];
  option?: {
    action: "add" | "update" | "delete";
    option_id?: string;
    label?: string;
    value?: string;
  };
  remove_definition?: boolean;
};

export type CrmSectionNodeInput = {
  parent_id?: string;
  node_id?: string;
  title?: string;
  order?: number;
  column_count?: number;
  is_repeatable?: boolean;
  fields?: CrmFieldNodeInput[];
};

export type CrmTabNodeInput = {
  node_id?: string;
  title?: string;
  order?: number;
  sections?: CrmSectionNodeInput[];
};

export type CrmLayoutNodeInput =
  | { tab: CrmTabNodeInput }
  | { section: CrmSectionNodeInput }
  | { field: CrmFieldNodeInput };

export const CRM_FILTER_KIND = {
  TEXT: "TEXT",
  NUMBER: "NUMBER",
  DATE: "DATE",
  CHECKBOX: "CHECKBOX",
  SINGLE_SELECT: "SINGLE_SELECT",
  MULTI_SELECT: "MULTI_SELECT",
} as const;

export type CrmFilterKind =
  (typeof CRM_FILTER_KIND)[keyof typeof CRM_FILTER_KIND];

/** How a MULTI_SELECT condition combines the picked options */
export const CRM_FILTER_MATCH = {
  ANY: 0,
  ALL: 1,
} as const;

export type CrmFilterMatch =
  (typeof CRM_FILTER_MATCH)[keyof typeof CRM_FILTER_MATCH];

/** A field the group list can be filtered on, as served by the filter catalog */
export type CrmFilterField = {
  field_key: string;
  label: string;
  field_type: CrmFieldType;
  filter_kind: CrmFilterKind;
  supported_operands: string[];
  help_text: string | null;
  options: Pick<CrmFieldOption, "label" | "value">[];
};

/**
 * One condition against one CRM field. Only the operands belonging to the
 * field's own kind may be set - the backend rejects the rest rather than
 * ignoring them.
 */
export type CrmFieldFilter = {
  field_key: string;
  equals?: string;
  contains?: string;
  min_value?: number;
  max_value?: number;
  date_from?: string;
  date_to?: string;
  is_checked?: boolean;
  values?: string[];
  match?: CrmFilterMatch;
};

export type CrmFilterFieldsResponse = {
  results: CrmFilterField[];
  total_pages: number;
  current_page: number;
  total_count: number;
  per_page: number;
};

/** A named, ordered set of field_keys that preselects export columns - global (admin-seeded,
 * is_personal false) or saved by the current user (is_personal true). */
export type CrmExportTemplate = {
  uuid: string;
  name: string;
  field_keys: string[];
  is_system_default: boolean;
  is_personal: boolean;
};

export type CrmExportFormat = "csv" | "xlsx";

export type CrmExportPreviewResponse = {
  row_count: number;
};

export type CrmExportResponse = {
  download_url: string;
};
